import { ILocalization } from '@shared-ui/localization-context';

import { iconMap } from 'models/hotel/map-types/icon-map';

import { UitkEGMapsQualifier } from 'uitk-react-map';

import {
  Bounds,
  BoundsComputedViewport,
  CenterAndZoom,
  ClusterObject,
  CoordinatesArray,
  EGMap,
  EGMapConfiguration,
  FeatureObject,
  i18nKeys,
  LatLngArray,
} from '@eg-maps/core';

import '@eg-maps/commons';

export const checkPropertyClusterLabelKey = (key: string, l10n: ILocalization, ...args: string[]): string | undefined =>
  key === i18nKeys.defaultClusterPropertyLabel ? l10n.formatText('egmaps.markers.cluster.available.text', args[0]) : undefined;

export const filterFeaturesInBounds = (mapMarkers: FeatureObject[], bounds: Bounds): FeatureObject[] =>
  mapMarkers.filter(marker => inBoundingBox(bounds.sw, bounds.ne, [marker.lat, marker.lng]));

export const inBoundingBox = (bottomLeft: LatLngArray, topRight: LatLngArray, point: LatLngArray) => {
  if (bottomLeft && topRight && point) {
    const [lat, lng] = point;
    const [trLat, trLng] = topRight;
    const [blLat, blLng] = bottomLeft;

    if (blLng < trLng) return lng >= blLng && lng <= trLng && lat >= blLat && lat <= trLat;

    // bbox crossing the dateline
    return (lng >= blLng || lng <= trLng) && lat >= blLat && lat <= trLat;
  }

  return false;
};

export const getLocale = (locale: string) => {
  const spanishPosa = ['es_ES', 'es_MX'];
  const englishPosa = ['en_US', 'en_AU', 'en_CA', 'en_GB'];
  const frenchPosa = ['fr_FR', 'fr_CA'];
  let loc = '';
  if (spanishPosa.includes(locale)) {
    loc = 'es_ES';
  } else if (frenchPosa.includes(locale)) {
    loc = 'fr_FR';
  } else if (englishPosa.includes(locale)) {
    loc = 'en_US';
  }

  return loc;
};

export const enrichMapMarkers = (mapMarkers: any) =>
  mapMarkers?.map((marker: any) => {
    // previous const { type = 'place', status... } would throw error on iconMap[marker.icon] is undefined
    const {
      type,
      status,
      qualifiers = undefined,
    } = iconMap[marker.icon as keyof typeof iconMap]
      ? { ...iconMap[marker.icon as keyof typeof iconMap] }
      : { type: 'place', status: 'unknown', qualifiers: undefined };

    return {
      id: marker.sheet?.id,
      type,
      status,
      qualifiers,
      lat: marker.position?.lat,
      lng: marker.position?.lng,
      text: type === 'place' ? marker.label : undefined,
      price: type === 'property' ? marker.label : undefined,
    };
  });

export const fetchPOIData = (locale: string, regionId?: string): Promise<FeatureObject[] | undefined> =>
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  new Promise(resolve =>
    fetch(`https://a.travel-assets.com/travel-assets-manager/srpmappois/${regionId}.json`)
      .then(response => response.json())
      .then(poiData => {
        if (poiData) {
          const filteredData = poiData.find((d: any) => d.locale === getLocale(locale));

          resolve(
            filteredData.destination.pois.map((poi: any) => {
              const coordinates = poi.coordinates;
              const latLng = coordinates.split(',');

              return {
                id: poi.id,
                type: 'place',
                status: 'unknown',
                lat: latLng[0],
                lng: latLng[1],
                name: poi.name,
                images: poi.images,
                description: poi.description,
              } as FeatureObject;
            })
          );
        }
        resolve(undefined);
      })
  );

export const buildConfig = (locale: string, mapId: string | null | undefined, translate: any) =>
  ({
    locale,
    googleMaps: {
      gmapsAddConfig: {
        mapId: mapId ?? '466ac5e186f00481',
      },
    },
    markers: {
      place: {
        zIndex: {
          unknown: 0,
        },
      },
      property: {
        zIndex: {
          [`${UitkEGMapsQualifier.NOT_CLUSTERED}`]: 0,
        },
      },
    },
    i18n: {
      formatFunction: translate,
    },
  } as EGMapConfiguration);

export const buildInitialViewport = (
  useMarkerViewState: boolean,
  enableBoundsComputation: boolean,
  mapCenter: any,
  markers: FeatureObject[],
  bounds?: Bounds,
  mapZoom?: number
) => {
  if (useMarkerViewState && markers?.length > 0) return markers.map(m => [m.lat, m.lng]) as CoordinatesArray;
  if (enableBoundsComputation)
    return {
      useBoundsComputation: true,
      options: {},
      coordinates: markers.map(m => [m.lat, m.lng]),
    } as BoundsComputedViewport;
  if (bounds) return bounds;
  if (mapCenter && mapCenter.lat && mapCenter.lng && mapZoom)
    return {
      center: { lat: mapCenter.lat, lng: mapCenter.lng },
      zoom: mapZoom,
    } as CenterAndZoom;

  if (markers?.length > 0) return markers.map(m => [m.lat, m.lng]) as CoordinatesArray;

  return undefined;
};

export const registerSelectionListener = (
  map: EGMap,
  onMarkerSelect?: (marker: FeatureObject) => void,
  onClusterSelect?: (marker: ClusterObject) => void
) => {
  if (onMarkerSelect || onClusterSelect) {
    map.addSelectionChangeListener(({ action, feature }) => {
      if (action === 'select') {
        if (feature.type === 'cluster') {
          onClusterSelect?.(feature);
        } else {
          onMarkerSelect?.(feature);
        }
      }
    });
  }
};

type ViewportChangeCallback = (center: LatLngArray, bounds: Bounds, zoom: number) => void;

export const registerViewportChangeListener = (
  map: EGMap,
  onZoomIn?: ViewportChangeCallback,
  onZoomOut?: ViewportChangeCallback,
  onMapPan?: ViewportChangeCallback
) => {
  if (onZoomIn || onZoomOut || onMapPan) {
    map.addBoundChangeListener(({ bounds, zoom, originalEvent }) => {
      switch (originalEvent) {
        case 'zoomIn':
        case 'zoomInKeyboard':
          onZoomIn?.(bounds.center, bounds, zoom);
          break;
        case 'zoomOut':
        case 'zoomOutKeyboard':
          onZoomOut?.(bounds.center, bounds, zoom);
          break;
        default:
          onMapPan?.(bounds.center, bounds, zoom);
          break;
      }
    });
  }
};
